import { INVOICE_STATUS } from '@aid/shared';
import type { CobroClienteResponse, InvoiceType, QueryCobrosDto } from '@aid/shared';

/**
 * Invoice table contract shared by the global list and Cliente 360:
 * - Canonical invoice keys are VIGENTE, PAGADA, and ANULADA; the UI presents them as
 *   Pendiente, Pagada, and Anulada respectively.
 * - Lines and Comment are intentionally omitted from both main tables because
 *   frozen lines are rare and historical comments are mostly generic.
 * - `generadoPor` remains an audit value with persisted origins `CRON`,
 *   `ADELANTO`, and `MANUAL`; this table does not mutate or reinterpret it.
 * - The existing detail/audit surface remains responsible for full lines and
 *   comments. This work unit does not rewrite historical comments or change
 *   cron/generator behavior.
 * - A future cron/comment work unit may expose structured origin metadata and
 *   decide whether a human-authored comment belongs in a list column.
 */

export const FACTURA_TABLE_SCOPE = {
  GLOBAL: 'global',
  CLIENTE: 'cliente',
} as const;

export type FacturaTableScope = (typeof FACTURA_TABLE_SCOPE)[keyof typeof FACTURA_TABLE_SCOPE];

export const FACTURA_TABLE_GROUP = {
  NONE: 'none',
  CLIENTE: 'cliente',
  ESTADO: 'estado',
} as const;

export type FacturaTableGroupBy = (typeof FACTURA_TABLE_GROUP)[keyof typeof FACTURA_TABLE_GROUP];

export const FACTURA_STATUS_KEY = {
  PENDIENTE: INVOICE_STATUS.VIGENTE,
  PAGADA: INVOICE_STATUS.PAGADA,
  ANULADA: INVOICE_STATUS.ANULADA,
  DESCONOCIDA: 'DESCONOCIDA',
} as const;

export type FacturaStatusKey = (typeof FACTURA_STATUS_KEY)[keyof typeof FACTURA_STATUS_KEY];

export type FacturaTableQueryFilters = Omit<QueryCobrosDto, 'limit' | 'offset'>;

export interface FacturaTableFilterState {
  idCliente: string;
  search: string;
  tipoFactura?: InvoiceType;
  idEstado?: number;
  fechaDesde?: string;
  fechaHasta?: string;
}

export const EMPTY_FACTURA_TABLE_FILTERS: FacturaTableFilterState = {
  idCliente: '',
  search: '',
};

export interface FacturasTableCapabilities {
  showClientFilter: boolean;
  showClientColumn: boolean;
  showStats: boolean;
  showGrouping: boolean;
  showPaginationSizeChanger: boolean;
  showCreate: boolean;
  compactEmptyState: boolean;
  /** Initial server sort applied when the user has not chosen one. The
   * client-scoped table starts on the newest emission (= period start after
   * the legacy normalization), descending. */
  defaultSort?: import('../../../lib/server-sort').ServerSortState;
}

export interface FacturaTableGroup {
  label: string;
  rows: CobroClienteResponse[];
}

export type FacturasTranslate = (key: string, options?: Record<string, unknown>) => string;

export interface FacturasTableGlobalProps {
  scope: typeof FACTURA_TABLE_SCOPE.GLOBAL;
}

export interface FacturasTableClienteProps {
  scope: typeof FACTURA_TABLE_SCOPE.CLIENTE;
  clienteId: number;
}

export type FacturasTableProps = FacturasTableGlobalProps | FacturasTableClienteProps;

const FACTURAS_TABLE_CAPABILITIES = {
  [FACTURA_TABLE_SCOPE.GLOBAL]: {
    showClientFilter: true,
    showClientColumn: true,
    showStats: true,
    showGrouping: true,
    showPaginationSizeChanger: false,
    showCreate: false,
    compactEmptyState: false,
  },
  [FACTURA_TABLE_SCOPE.CLIENTE]: {
    showClientFilter: false,
    showClientColumn: false,
    showStats: false,
    showGrouping: false,
    showPaginationSizeChanger: true,
    showCreate: true,
    compactEmptyState: true,
    defaultSort: { sortBy: 'fechaCobro', sortOrder: 'desc' },
  },
} satisfies Record<FacturaTableScope, FacturasTableCapabilities>;

export function getFacturasTableCapabilities(scope: FacturaTableScope): FacturasTableCapabilities {
  return FACTURAS_TABLE_CAPABILITIES[scope];
}
